import { ActivityEventIcon } from '@/components/shared/ActivityEventIcon/ActivityEventIcon';
import { ClientWalletLink } from '@/components/shared/ClientWalletLink';
import { CollectionOfferTraits } from '@/components/shared/CollectionOfferTraits/CollectionOfferTraits';
import { DateTableCell, PriceTableCell } from '@/components/shared/Table';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { isAttributeOrder, isBurnActivity, isCollectionOrder } from '@/hooks/use-user-activity';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useENSResolver } from '@/hooks/useENSResolver';
import { getChainExplorerName, getChainExplorerUrl } from '@/utils/chainExplorers';
import { routes } from '@/utils/routes';
import { Flex, Td, Text, Tr } from '@chakra-ui/react';
import { formatAddress } from '@onbeam/utils';
import { CollectionOfferIcon, DiagonalArrowIcon } from '@sphere/icons';
import { ButtonLink, QuantityLabel, TableRowMediaItem } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { ActivityData, ActivityType, getActivityEventColor, getActivityEventTitle } from '../utils';

type Props = {
  hideItemColumn?: boolean;
  hideTransactionColumn?: boolean;
  hideToAddressColumn?: boolean;
} & ActivityData;

export const ActivityItemDesktop = ({
  hideItemColumn,
  hideTransactionColumn,
  hideToAddressColumn,
  ...activity
}: Props) => {
  const marketplaceChain = useMarketplaceChain();
  const { t } = useTranslation('activity');
  const { userAddress } = useAuthentication();
  const { displayName: toDisplayName } = useENSResolver(activity.toAddress);
  const { displayName: fromDisplayName } = useENSResolver(activity.fromAddress);
  const isCollectionOffer = isCollectionOrder(activity);
  const isBurn = isBurnActivity(activity);

  return (
    <Tr>
      {!hideItemColumn && (
        <Td px="space.16">
          {isCollectionOffer ? (
            <TableRowMediaItem
              href={routes.collections.detail(
                activity.collection?.collectionId ?? '',
                marketplaceChain.routePrefix,
              )}
              title={formatAddress(activity.collection?.collectionName)}
              imageSrc={activity.collection?.collectionImage}
              subtitle={
                isAttributeOrder(activity) ? (
                  // @ts-expect-error - Type is wrong
                  <CollectionOfferTraits traits={[activity.order?.criteria?.data.attribute]} />
                ) : undefined
              }
              rounded="full"
              coverImage
              quantity={activity.amount}
            />
          ) : (
            <TableRowMediaItem
              href={
                activity.token
                  ? routes.nft.detail(
                      activity.collection?.collectionId ?? '',
                      activity.token.tokenId ?? '',
                      marketplaceChain.routePrefix,
                    )
                  : ''
              }
              title={activity.token?.tokenName || `#${activity.token?.tokenId}`}
              imageSrc={activity.token?.tokenImage || activity.collection?.collectionImage}
              subtitle={
                <Text fontSize="sm" color="gray.5" noOfLines={1} wordBreak="break-all">
                  {formatAddress(activity.collection?.collectionName)}
                </Text>
              }
              quantity={activity.amount}
              {...(!activity.token?.tokenImage &&
                activity.collection?.collectionImage && {
                  rounded: 'full',
                  coverImage: true,
                })}
            />
          )}
        </Td>
      )}

      <Td>
        <Flex
          color={getActivityEventColor(activity.type as ActivityType)}
          alignItems="center"
          gap="space.8"
        >
          {isCollectionOffer ? (
            <CollectionOfferIcon boxSize={rem(20)} />
          ) : (
            <ActivityEventIcon type={isBurn ? 'burn' : (activity.type as ActivityType)} />
          )}
          <Text fontSize="md">
            {getActivityEventTitle(
              (isCollectionOffer
                ? `collection-${activity.type}`
                : isBurn
                ? 'burn'
                : activity.type) as ActivityType,
              t,
            )}
          </Text>
          {hideItemColumn && !!activity.amount && activity.amount > 1 && (
            <QuantityLabel quantity={activity.amount} />
          )}
        </Flex>
      </Td>
      <PriceTableCell
        amount={activity.price?.amount?.decimal}
        currency={activity.price?.currency?.symbol}
        usdPrice={activity.price?.amount?.usd}
        align="right"
      />
      <Td>
        {activity.fromAddress ? (
          <ClientWalletLink
            userAddress={userAddress}
            walletAddress={activity.fromAddress}
            username={fromDisplayName}
            href={routes.profile(activity.fromAddress, marketplaceChain.routePrefix)}
          />
        ) : (
          '--'
        )}
      </Td>
      {!hideToAddressColumn && (
        <Td>
          {activity.toAddress ? (
            <ClientWalletLink
              userAddress={userAddress}
              walletAddress={activity.toAddress}
              username={toDisplayName}
              href={routes.profile(activity.toAddress, marketplaceChain.routePrefix)}
            />
          ) : (
            '--'
          )}
        </Td>
      )}
      <DateTableCell date={new Date((activity.timestamp || 0) * 1000).toISOString()} />
      {!hideTransactionColumn && (
        <Td textAlign="right">
          {!!marketplaceChain.id && activity.txHash && (
            <ButtonLink
              title={getChainExplorerName(marketplaceChain.id)}
              href={getChainExplorerUrl(marketplaceChain.id, activity.txHash, 'tx')}
              target="_blank"
              rel="noopener noreferrer"
              boxSize={rem(36)}
              dislay="block"
              p={0}
              minW="unset"
              bg="transparent"
              mr="space.8"
              transition="background 120ms ease-in-out"
              _hover={{ bg: 'gray.4' }}
            >
              <DiagonalArrowIcon color="white" boxSize={rem(16)} />
            </ButtonLink>
          )}
        </Td>
      )}
    </Tr>
  );
};
